import {
  Box,
  Button,
  ColorSwatch,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import type { Session } from "../lib/api";
import {
  AVATAR_COLOR_OPTIONS,
  AvatarPreviewCanvas,
} from "../components/AvatarPreviewCanvas";

interface Props {
  session: Session;
  onComplete: (name: string, color: string) => Promise<void>;
}

export function OnboardingScreen({ session, onComplete }: Props) {
  const [name, setName] = useState(session.displayName);
  const [color, setColor] = useState(session.avatarColor);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim().slice(0, 30);
    if (!trimmed) {
      setError("Please enter a display name.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onComplete(trimmed, color);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      mih="100vh"
      style={{
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, #f0f2f5 0%, #fafbfc 100%)",
        padding: 20,
      }}
    >
      <Box
        w={480}
        p="xl"
        style={{
          background: "#ffffff",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.10)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
          borderRadius: 16,
        }}
      >
        <Stack gap="md">
          <Title order={2} c="dark" lts="0.02em">
            Set Up Your Avatar
          </Title>
          <Text c="gray.6" size="sm">
            Pick your display name and color, then enter the world.
          </Text>

          <TextInput
            label="Display Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={30}
          />

          <Box
            style={{
              height: 220,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(180deg, #edf2f7 0%, #f8f9fa 100%)",
            }}
          >
            <AvatarPreviewCanvas color={color} />
          </Box>

          <SimpleGrid cols={5} spacing={8}>
            {AVATAR_COLOR_OPTIONS.map((c) => (
              <ColorSwatch
                key={c}
                color={c}
                size={34}
                onClick={() => setColor(c)}
                style={{
                  cursor: "pointer",
                  outline:
                    color === c
                      ? "2px solid #ff922b"
                      : c === "#ffffff"
                      ? "1px solid rgba(0,0,0,0.15)"
                      : "none",
                  outlineOffset: 2,
                  margin: "0 auto",
                }}
              />
            ))}
          </SimpleGrid>

          {error && (
            <Text c="red.5" size="sm">
              {error}
            </Text>
          )}

          <Group justify="flex-end">
            <Button loading={saving} onClick={() => void submit()}>
              Enter World
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
}
